import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Search, X, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { z } from 'zod'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import type { KnowledgeBase } from '@/hooks'

interface KnowledgeBaseSettingsDialogProps {
    knowledgeBase: KnowledgeBase
    isOpen: boolean
    onClose: () => void
}

// Zod Schemas
const permissionSchema = z.enum(['admin', 'editor', 'viewer'] as const)

const editorSchema = z.object({
    id: z.string(),
    name: z.string(),
    position: z.string().optional(),
    avatar: z.string().optional(),
    permission: permissionSchema,
})

const settingsSchema = z.object({
    editors: z.array(editorSchema),
})

type SettingsFormValues = z.infer<typeof settingsSchema>

// Mock data for demonstration - in real app this would come from API
const MOCK_EDITORS: z.infer<typeof editorSchema>[] = [
    { id: '1', name: '张三', position: '产品经理', permission: 'admin' },
    { id: '2', name: '李四', position: '技术总监', permission: 'editor' },
    { id: '3', name: '王五', position: '前端开发', permission: 'editor' },
]

export function KnowledgeBaseSettingsDialog({
    knowledgeBase,
    isOpen,
    onClose,
}: KnowledgeBaseSettingsDialogProps) {
    const [searchQuery, setSearchQuery] = useState('')
    const toast = useToast()
    const [isSaving, setIsSaving] = useState(false)

    // Initialize Form
    const form = useForm<SettingsFormValues>({
        resolver: zodResolver(settingsSchema),
        defaultValues: {
            editors: MOCK_EDITORS,
        },
        mode: 'onChange',
    })

    const { fields, remove, replace } = useFieldArray({
        control: form.control,
        name: 'editors',
    })

    // Reset form when dialog opens
    useEffect(() => {
        if (isOpen) {
            // In a real app, you would fetch fresh data here
            replace(MOCK_EDITORS)
        }
    }, [isOpen, replace])

    const handleAddMember = () => {
        toast.info('添加成员功能在演示模式下暂不可用')
    }

    const onSubmit = async (data: SettingsFormValues) => {
        setIsSaving(true)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))
        console.log('Saving settings:', data)
        setIsSaving(false)
        toast.success('设置已保存')
        onClose()
    }

    return (
        <Dialog open={isOpen} onOpenChange={(_) => !isSaving && onClose()}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>知识库设置 - {knowledgeBase.name}</DialogTitle>
                    <DialogDescription>
                        管理知识库的成员和权限设置。
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="搜索成员..."
                                className="pl-9"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <Button onClick={handleAddMember} type="button" variant="outline">
                            <Plus className="mr-2 h-4 w-4" />
                            添加成员
                        </Button>
                    </div>

                    <ScrollArea className="h-[300px] pr-4">
                        <form id="kb-settings-form" onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-4">
                            {/* Rendering Strategy: Map ALL fields but display:none if unused. 
                                This preserves index for Controller usage. */}
                            {fields.map((field, index) => {
                                const isVisible =
                                    field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    field.position?.toLowerCase().includes(searchQuery.toLowerCase())

                                if (!isVisible) return <div key={field.id} className="hidden" />

                                return (
                                    <div
                                        key={field.id}
                                        className="flex items-center justify-between gap-4 rounded-lg border p-3"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Avatar>
                                                <AvatarImage src={field.avatar} />
                                                <AvatarFallback>
                                                    {field.name.slice(0, 1)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <div className="font-medium">{field.name}</div>
                                                {field.position && (
                                                    <div className="text-xs text-muted-foreground">
                                                        {field.position}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Controller
                                                control={form.control}
                                                name={`editors.${index}.permission`}
                                                render={({ field: selectField }) => (
                                                    <Select
                                                        value={selectField.value}
                                                        onValueChange={selectField.onChange}
                                                    >
                                                        <SelectTrigger className="w-[100px] h-8 text-xs">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="admin">管理员</SelectItem>
                                                            <SelectItem value="editor">编辑者</SelectItem>
                                                            <SelectItem value="viewer">查看者</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />

                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                onClick={() => remove(index)}
                                                type="button"
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                            {fields.length === 0 && (
                                <div className="py-8 text-center text-sm text-muted-foreground">
                                    暂无成员
                                </div>
                            )}
                        </form>
                    </ScrollArea>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSaving} type="button">
                        取消
                    </Button>
                    <Button type="submit" form="kb-settings-form" disabled={isSaving}>
                        {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        保存更改
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
